import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { useMutation } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import { FormProvider, useForm } from 'react-hook-form';

import { voidPaymentMutation } from '@drivingdoc/api-client/generated/react-query';

import TextInput from 'components/Forms/TextInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import FeedbackDialog from '../../FeedbackDialog';
import { VoidFormValues, VoidSchema } from './VoidSchema';

interface VoidPaymentDialogPayload {
  id: string;
}

function VoidPaymentDialog({ payload, open, onClose }: DialogProps<VoidPaymentDialogPayload>) {
  const { id } = payload;

  const dialogs = useDialogs();

  const voidMutation = useMutation(voidPaymentMutation());

  const form = useForm<VoidFormValues>({
    mode: 'all',
    resolver: zodResolver(VoidSchema),
  });

  const { handleSubmit } = form;

  const onSubmit = async ({ reason }: VoidFormValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm('Are you sure you want to void this payment? This cannot be reverted.', {
        okText: 'Void',
        cancelText: 'Cancel',
        severity: 'error',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      // void the payment
      await voidMutation.mutateAsync({
        path: { id },
        body: { reason },
      });

      // close the dialog
      await onClose();

      // show success message
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Payment Voided',
      });
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error voiding payment',
        message: getApiErrorMessage(error),
      });
    }
  };

  return (
    <Dialog fullWidth open={open} onClose={() => onClose()}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <DialogTitle>Void Payment</DialogTitle>
          <DialogContent>
            <Grid container rowSpacing={3} columnSpacing={1}>
              <Grid size={12}>
                <TextInput
                  name="reason"
                  label="Reason"
                  multiline
                  helperText="Provide a brief description why the payment is being voided."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose()}>Cancel</Button>
            <Button color="error" type="submit" disabled={voidMutation.isPending}>
              Void
            </Button>
          </DialogActions>
        </FormProvider>
      </form>
      <Backdrop open={voidMutation.isPending} sx={{ color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default VoidPaymentDialog;
